// Credits Service
// Manages credit balance, transactions, and rewards

import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  increment,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import {
  CreditBalance,
  CreditTransaction,
  CREDIT_COSTS,
  CREDIT_REWARDS
} from '../types/credits.types';

/**
 * Get user's credit balance
 */
export async function getCreditBalance(userId: string): Promise<CreditBalance | null> {
  const ref = doc(db, 'credits', userId);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) {
    return null;
  }
  
  const data = snap.data();
  return {
    userId,
    balance: data.balance ?? 0,
    lifetimeEarned: data.lifetimeEarned ?? 0,
    lifetimeSpent: data.lifetimeSpent ?? 0,
    lastUpdated: data.lastUpdated?.toDate() ?? new Date()
  };
}

/**
 * Initialize credit balance for new user
 */
export async function initializeCreditBalance(userId: string, initialCredits: number = 10): Promise<void> {
  const ref = doc(db, 'credits', userId);
  await setDoc(ref, {
    balance: initialCredits,
    lifetimeEarned: initialCredits,
    lifetimeSpent: 0,
    lastUpdated: serverTimestamp()
  });
  
  // Record transaction
  await recordTransaction(userId, initialCredits, 'reward', 'Welkom bonus');
}

/**
 * Spend credits (returns false if insufficient balance)
 */
export async function spendCredits(
  userId: string,
  amount: number,
  type: CreditTransaction['type'],
  description: string,
  relatedEntity?: CreditTransaction['relatedEntity']
): Promise<boolean> {
  const ref = doc(db, 'credits', userId);
  
  try {
    const result = await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);
      
      if (!snap.exists()) {
        throw new Error('Credit balance not found');
      }
      
      const currentBalance = snap.data().balance ?? 0;
      
      if (currentBalance < amount) {
        return false; // Insufficient balance
      }
      
      // Deduct credits
      transaction.update(ref, {
        balance: increment(-amount),
        lifetimeSpent: increment(amount),
        lastUpdated: serverTimestamp()
      });
      
      return true;
    });
    
    if (result) {
      // Record transaction
      await recordTransaction(userId, -amount, type, description, relatedEntity);
    }
    
    return result;
  } catch (error) {
    console.error('[creditsService] Error spending credits:', error);
    return false;
  }
}

/**
 * Award credits
 */
export async function awardCredits(
  userId: string,
  amount: number,
  type: CreditTransaction['type'],
  description: string,
  relatedEntity?: CreditTransaction['relatedEntity']
): Promise<void> {
  const ref = doc(db, 'credits', userId);
  
  // Check if balance exists, if not initialize
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await initializeCreditBalance(userId, 0);
  }
  
  // Add credits
  await updateDoc(ref, {
    balance: increment(amount),
    lifetimeEarned: increment(amount),
    lastUpdated: serverTimestamp()
  });
  
  // Record transaction
  await recordTransaction(userId, amount, type, description, relatedEntity);
}

/**
 * Record credit transaction for history
 */
async function recordTransaction(
  userId: string,
  amount: number,
  type: CreditTransaction['type'],
  description: string,
  relatedEntity?: CreditTransaction['relatedEntity']
): Promise<void> {
  await addDoc(collection(db, 'creditTransactions'), {
    userId,
    amount,
    type,
    description,
    relatedEntity: relatedEntity ?? null,
    createdAt: serverTimestamp()
  });
}

/**
 * Get recent transactions
 */
export async function getRecentTransactions(
  userId: string,
  limitCount: number = 10
): Promise<CreditTransaction[]> {
  const q = query(
    collection(db, 'creditTransactions'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  
  const snap = await getDocs(q);
  return snap.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate() ?? new Date()
  } as CreditTransaction));
}

/**
 * Check if user can afford a cost
 */
export async function canAfford(userId: string, cost: number): Promise<boolean> {
  const balance = await getCreditBalance(userId);
  return balance ? balance.balance >= cost : false;
}

// Export costs for easy access
export { CREDIT_COSTS, CREDIT_REWARDS };
